import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Tooltip,
  Paper,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import SlackIcon from "@/assets/icons/slack-1.svg?react";
import TeamsIcon from "@/assets/icons/microsoft-teams.svg?react";
import type { IntegrationState } from "../types";

interface SlackTeamsConfigProps {
  platform: string;
  projectDetails: any;
  state: IntegrationState;
  configData: any;
  handleWebhookChange: (newValue: any) => void;
  onNavigateToCompanySettings: () => void;
}

export default function SlackTeamsConfig({
  platform,
  projectDetails,
  state,
  configData,
  handleWebhookChange,
  onNavigateToCompanySettings,
}: SlackTeamsConfigProps) {
  const { t } = useTranslation(["bugherd"]);

  // Switching channel warning
  const existingChannel = projectDetails?.communicationChannel;
  const showChannelWarning =
    existingChannel &&
    existingChannel !== "NONE" &&
    existingChannel !== platform;

  // Get the config list - handle both direct array and localData structure
  const configList = Array.isArray(configData)
    ? configData
    : configData?.localData || [];

  // Filter webhooks based on the selected platform
  const filteredWebhooks = configList.filter(
    (config: any) => config.type === platform,
  );

  // Find currently selected webhook
  const savedWebhookUrl = state.communicationChannelData?.webhookUrl;
  const selectedWebhook = filteredWebhooks.find((config: any) => {
    const configUrl = Array.isArray(config.webhookUrl)
      ? config.webhookUrl[0]
      : config.webhookUrl;
    const savedUrl = Array.isArray(savedWebhookUrl)
      ? savedWebhookUrl[0]
      : savedWebhookUrl;
    return String(configUrl).trim() === String(savedUrl).trim();
  });

  const PlatformIcon = platform === "SLACK" ? SlackIcon : TeamsIcon;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2.5,
        borderRadius: "10px",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: (theme) => theme.palette.background.paper,
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={600}
        fontSize="13px"
        mb={2}
        color="text.secondary"
      >
        {t("Communication Channel")}
      </Typography>

      {/* Warning when switching Communication Channel */}
      {showChannelWarning && (() => {
        const existingName =
          existingChannel === "SLACK" ? "Slack" : "Microsoft Teams";
        const newName =
          platform === "SLACK" ? "Slack" : "Microsoft Teams";

        return (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "8px",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 152, 0, 0.1)"
                  : "#fff3e0",
              border: (theme) =>
                `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 152, 0, 0.3)" : "#ffcc80"}`,
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <Box flex={1}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mb={0.5}
                >
                  <WarningAmberIcon
                    sx={{ color: "#f57c00", fontSize: 18 }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    fontSize="13px"
                    color="#e65100"
                  >
                    {t("Switching Communication Channel")}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  fontSize="12px"
                  color="text.secondary"
                  lineHeight={1.5}
                >
                  {t("switchingChannelWarning", {
                    existing: existingName,
                    new: newName,
                  })}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })()}

      {filteredWebhooks.length > 0 ? (
        <Autocomplete
          options={filteredWebhooks}
          getOptionLabel={(option: any) => {
            const channelType =
              option.type === "SLACK" ? "Slack" : "Microsoft Teams";
            const webhookUrl = String(option.webhookUrl || "");
            const truncatedUrl =
              webhookUrl.length > 70
                ? webhookUrl.substring(0, 70) + "..."
                : webhookUrl;
            return `${option.name || channelType} - ${truncatedUrl}`;
          }}
          value={selectedWebhook || null}
          onChange={(_, newValue) => handleWebhookChange(newValue)}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "100%",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      flexGrow: 1,
                      fontWeight: 500,
                      fontSize: "13px",
                    }}
                  >
                    {option.name ||
                      t("{{type}} Webhook", { type: option.type })}
                  </Typography>
                  {selected && (
                    <Check
                      sx={{
                        color: (theme) => theme.palette.primary.main,
                        ml: 1,
                      }}
                    />
                  )}
                </Box>
                <Tooltip
                  title={option.webhookUrl || ""}
                  placement="bottom-start"
                  arrow
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      ml: 0,
                      fontSize: "11px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                      cursor: "pointer",
                    }}
                  >
                    {option.webhookUrl}
                  </Typography>
                </Tooltip>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t(
                platform === "SLACK"
                  ? "Select Slack Webhook"
                  : "Select Teams Webhook",
              )}
              placeholder={t(
                platform === "SLACK"
                  ? "Choose a Slack webhook"
                  : "Choose a Teams webhook",
              )}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.06)"
                            : "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1,
                        ml: 0.5,
                        flexShrink: 0,
                        "& svg": {
                          width: 20,
                          height: 20,
                        },
                      }}
                    >
                      <PlatformIcon />
                    </Box>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      ) : (
        <Box
          sx={{
            p: 3,
            textAlign: "center",
            borderRadius: "8px",
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.03)"
                : "#f9f9f9",
            border: (theme) => `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            fontSize="13px"
          >
            {t(
              platform === "SLACK"
                ? "No Slack webhooks configured."
                : "No Microsoft Teams webhooks configured.",
            )}{" "}
            {t("Click")}{" "}
            <span
              onClick={onNavigateToCompanySettings}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: 600,
              }}
            >
              {t("here")}
            </span>{" "}
            {t("to configure webhooks.")}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
